import { Trans } from "@lingui/macro";
import { InfoCircle } from "@winamp/icons/InfoCircle.js";
import { Tooltip } from "@winamp/ui/components/Tooltip/Tooltip.js";
import { TooltipContent } from "@winamp/ui/components/Tooltip/TooltipContent.js";
import { TooltipTrigger } from "@winamp/ui/components/Tooltip/TooltipTrigger.js";
import { classNames } from "@winamp/ui/utils/classNames.js";
import {
  type ServiceIssue,
  canCreateService,
  canSubmitService,
} from "~/models/service-issue";

interface IEntry {
  audioUid: string;
  issues: Array<ServiceIssue>;
  title?: string | null;
  friendlyName: string;
}

interface IServiceCandidateIssueProps {
  candidate: { entries: Array<IEntry> };
  additionalChecks?: {
    canSubmit?: boolean;
    canCreate?: boolean;
  };
  className?: string;
}

export function ServiceCandidateIssue({
  additionalChecks,
  candidate,
  className,
}: IServiceCandidateIssueProps) {
  const canCreateRelease =
    canCreateService(candidate.entries) &&
    additionalChecks?.canCreate !== false;
  const canSubmitRelease =
    canSubmitService(candidate.entries) &&
    additionalChecks?.canSubmit !== false;

  if (canCreateRelease && canSubmitRelease) {
    return null;
  }

  return (
    <div
      className={classNames(
        "text-sm/5 font-normal",
        {
          "text-error": !canCreateRelease,
          "text-warning": canCreateRelease && !canSubmitRelease,
        },
        className,
      )}
    >
      {!canCreateRelease ? (
        <div className="flex gap-1 items-center">
          {
            <span>
              <Trans>Creation not allowed</Trans>
            </span>
          }
          <Tooltip placement="top">
            <TooltipTrigger>
              <InfoCircle className="size-3" />
            </TooltipTrigger>
            <TooltipContent className="w-[240px] flex flex-col gap-3">
              {candidate.entries.flatMap((entry) => (
                <div key={entry.audioUid} className="flex flex-col gap-1">
                  <span className="font-semibold text-sm text-error truncate">
                    {entry.title ?? entry.friendlyName}
                  </span>
                  <ul className="list-disc list-inside">
                    {entry.issues
                      .filter((issue) => issue.preventsCreation)
                      .map((issue) => (
                        <li
                          key={issue.id}
                          className="text-sm/5 font-normal text-error"
                        >
                          {issue.name}
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            </TooltipContent>
          </Tooltip>
        </div>
      ) : (
        <span>
          <Trans>Information missing</Trans>
        </span>
      )}
    </div>
  );
}
